import API from "@/api";
import { eventBus } from "@/eventBus.js";
import toasted from "@/utils/functions/toasted";

const SET_STEP = "SET_STEP";
const SET_LEGAL_STEP = "SET_LEGAL_STEP";
const SET_ACCOUNTABLE_TYPE = "SET_ACCOUNTABLE_TYPE";
const SET_LEGAL = "SET_LEGAL";
const SET_PHYSICAL = "SET_PHYSICAL";
const SET_ACCOUNT = "SET_ACCOUNT";
const SET_PROFILE = "SET_PROFILE";
const SET_PROFILE_PICTURE = "SET_PROFILE_PICTURE";
const SET_LEGAL_REPRESENTATIVE = "SET_LEGAL_REPRESENTATIVE";
const SET_LEGAL_REPRESENTATIVE_ADDRESS = "SET_LEGAL_REPRESENTATIVE_ADDRESS";
const SET_PHONES = "SET_PHONES";
const SET_FIELD_ERRORS = "SET_FIELD_ERRORS";
const SET_DISPLAY_IBGE_SELECTS = "SET_DISPLAY_IBGE_SELECTS";
const RESET_STATE = "RESET_STATE";

const getDefaultState = () => {
  return {
    step: 0,
    legal_step: 0,
    accountableType: null,
    legal: null,
    legal_representative: null,
    legal_representative_address: null,
    physical: null,
    account: null,
    profile: null,
    profile_picture: null,
    phones: [],
    fieldErrors: [],
    displayIBGESelects: true
  };
};

const PHYSICAL_STEP_ERRORS = { cpf: 3, email: 1, birthdate: 2 };
const LEGAL_STEP_ERRORS = {
  cnpj: "3/2",
  cpf: "3/0",
  birthdate: "3/0",
  email: "1/0"
};

export default {
  namespaced: true,
  state: getDefaultState(),
  getters: {},
  actions: {
    prevStep({ state, commit }) {
      if (!state.step) return;
      let step = state.step - 1;
      if (state.accountableType === "legal" && step === 3) step--;
      commit("SET_STEP", step);
    },
    nextStep({ state, commit }) {
      let step = state.step + 1;
      if (state.accountableType === "legal" && step === 2) step++;
      commit("SET_STEP", step);
    },
    prevLegalStep({ state, commit }) {
      if (!state.legal_step) return;
      commit("SET_LEGAL_STEP", state.legal_step - 1);
    },
    nextLegalStep({ state, commit }) {
      commit("SET_LEGAL_STEP", state.legal_step + 1);
    },
    setAccountableType({ commit }, type) {
      commit("SET_ACCOUNTABLE_TYPE", type);
      commit("SET_STEP", 1);
    },
    setLegal({ commit }, payload) {
      commit("SET_LEGAL", payload);
    },
    setLegalRepresentative({ state, dispatch, commit }, payload) {
      const { first_name, last_name, birthdate, phone } = payload;
      dispatch("setAccount", { ...state.account, first_name, last_name });
      dispatch("setProfile", { ...state.profile, birthdate });
      dispatch("setPhones", [phone]);
      commit("SET_LEGAL_REPRESENTATIVE", payload);
    },
    setLegalRepresentativeAddress({ state, dispatch, commit }, payload) {
      const { city, state: addressState } = payload;
      dispatch("setProfile", { ...state.profile, city, state: addressState });
      commit("SET_LEGAL_REPRESENTATIVE_ADDRESS", payload);
    },
    setPhysical({ commit }, payload) {
      commit("SET_PHYSICAL", payload);
    },
    setAccount({ commit }, payload) {
      commit("SET_ACCOUNT", payload);
    },
    setProfile({ state, commit, dispatch }, payload) {
      if (
        state.profile &&
        state.profile.profile_picture != payload.profile_picture
      )
        dispatch("setProfilePicture", null);
      commit("SET_PROFILE", payload);
    },
    setProfilePicture({ commit }, payload) {
      commit("SET_PROFILE_PICTURE", payload);
    },
    setPhones({ commit }, payload) {
      commit("SET_PHONES", payload);
    },
    setFieldErrors({ commit, dispatch }, payload) {
      let errors = payload.map(el => {
        let field = el.source.pointer.split("/").pop();
        let title = el.title;

        return { field, title };
      });
      commit("SET_FIELD_ERRORS", errors);
      dispatch("setStepError");
    },
    async register({ commit, dispatch, state }) {
      try {
        if (!state.profile_picture) await dispatch("uploadProfilePicture");

        state.accountableType === "physical"
          ? await API.Physicals.create(state)
          : await API.Legals.create(state);

        let { email, password } = state.account;

        await dispatch(
          "user/authentication",
          { email, password },
          { root: true }
        );

        const termsOfUse = await API.AccountTermsOfUse.getTermsOfUse();

        await dispatch(
          "accountTermsOfUse/submitTermsOfUseResponse",
          { accepted: true, termsOfUseId: termsOfUse.id },
          { root: true }
        );

        await dispatch("user/refreshUser", null, { root: true });

        await dispatch("RDStation/conversion", null, { root: true });
        await dispatch("user/sendEmailVerification", null, { root: true });
        dispatch("resetState");
      } catch ({ status, message, data }) {
        if (status != 422) throw { duration: 5000, message };

        dispatch("setFieldErrors", data);
        message =
          "Revise os campos destacados, avance as etapas e clique em cadastrar novamente.";

        throw { duration: 10000, message };
      }
    },
    async uploadProfilePicture({ dispatch, state }) {
      try {
        toasted.withLoader("Upando imagem de perfil, aguarde...");
        let profile_picture = await API.Cloudinary.upload(
          state.profile.profile_picture
        );
        await dispatch("setProfilePicture", profile_picture);
        eventBus.$toasted.clear();
      } catch (e) {
        console.log(e);
        eventBus.$toasted.error(
          "Ops, ocorreu um erro ao salvar a imagem, tente novamente mais tarde!"
        );
      }
    },

    setStepError({ state, commit }) {
      let { fieldErrors } = state;

      const getLowest = arr => arr.sort((a, b) => (a < b ? -1 : 1))[0];
      const getLowestOfArray = arr =>
        arr
          .sort((a, b) => (a[0] < b[0] ? -1 : 1))
          .sort((a, b) => (a[1] < b[1] ? -1 : 1))[0];

      const getPhysicalStepError = () => {
        let steps = fieldErrors
          .map(({ field }) => PHYSICAL_STEP_ERRORS[field])
          .filter(el => el != undefined);
        let step = steps.length ? getLowest(steps) : 1;
        return [step, 0];
      };

      const getLegalStepError = () => {
        let steps = fieldErrors
          .map(({ field }) => LEGAL_STEP_ERRORS[field])
          .filter(el => el != undefined);
        steps = steps.map(el => el.split("/"));
        return getLowestOfArray(steps);
      };

      let steps =
        state.accountableType === "physical"
          ? getPhysicalStepError()
          : getLegalStepError();

      commit("SET_STEP", steps[0]);
      commit("SET_LEGAL_STEP", steps[1]);
    },

    hideIBGESelects({ dispatch, commit }) {
      dispatch("clearStateAndCity");
      commit("SET_DISPLAY_IBGE_SELECTS", false);
    },

    clearStateAndCity({ state, dispatch }) {
      let resource =
        state.accountableType === "physical"
          ? { action: "setProfile", attribute: "profile" }
          : {
              action: "setLegalRepresentative",
              attribute: "legal_representative"
            };

      dispatch(resource.action, {
        ...state[resource.attribute],
        state: "",
        city: ""
      });
    },

    resetState({ commit }) {
      commit("RESET_STATE");
    }
  },
  mutations: {
    [SET_STEP](state, payload) {
      state.step = parseInt(payload);
    },
    [SET_LEGAL_STEP](state, payload) {
      state.legal_step = parseInt(payload);
    },
    [SET_ACCOUNTABLE_TYPE](state, payload) {
      state.accountableType = payload;
    },
    [SET_LEGAL](state, payload) {
      state.legal = payload;
    },
    [SET_LEGAL_REPRESENTATIVE](state, payload) {
      state.legal_representative = payload;
    },
    [SET_LEGAL_REPRESENTATIVE_ADDRESS](state, payload) {
      state.legal_representative_address = payload;
    },
    [SET_PHYSICAL](state, payload) {
      state.physical = payload;
    },
    [SET_ACCOUNT](state, payload) {
      state.account = payload;
    },
    [SET_PROFILE](state, payload) {
      state.profile = payload;
    },
    [SET_PROFILE_PICTURE](state, payload) {
      state.profile_picture = payload;
    },
    [SET_PHONES](state, payload) {
      state.phones = payload;
    },
    [SET_FIELD_ERRORS](state, payload) {
      state.fieldErrors = payload;
    },
    [SET_DISPLAY_IBGE_SELECTS](state, payload) {
      state.displayIBGESelects = payload;
    },
    [RESET_STATE](state) {
      Object.assign(state, getDefaultState());
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/signUp.js